// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react';
import jsPDF from 'jspdf';
import { Document as DocxDocument, Packer, Paragraph, TextRun, HeadingLevel, ImageRun, Table, TableRow, TableCell, WidthType, VerticalAlign, BorderStyle, PageBreak, AlignmentType } from 'docx';
import PptxGenJS from 'pptxgenjs';
// FIX: Use relative path to resolve types module.
import { Presentation, Slide, LessonPlan, TeachingModel, UserSettings, Quiz, Question, MatrixRow, COGNITIVE_LEVELS, QuestionType, SpecificationTopic, ImageResource, VideoResource, Homework } from '../../types';
import { ArrowDownTrayIcon, ChevronDownIcon, SparklesIcon, ArrowPathIcon, ShareIcon } from './Icons';
import Spinner from './Spinner';
import { embedInterFontForPdf } from '../../utils/pdfUnicodeFont';
import { exportSkknPdf, exportSkknDocx } from '../../utils/skknExport';
import type { SKKNData } from '../../data/skknSampleData';
import html2canvas from 'html2canvas';
import {
  fetchImagesAsBase64Batch,
  generateVideo,
  getVideoOperation,
  getVideoDownloadUrl,
  downloadFromApi,
  formatAssetUrl,
  downloadPdfFromBackend,
  downloadDocxFromBackend,
  exportSlidesToPptx,
  upgradePresentationToHtml5,
  fetchResourceById,
} from '../../services/apiService';
import { ShareModal } from './ShareModal';
import { APP_MODE, CreditCost } from '../../config';
import { checkCanRemoveWatermark } from '../../utils/userUtils';
import { mathToPlainText } from '../../utils/mathMixedText';
import { createPlainMathParagraphs, type DocxFieldParagraphOptions } from '../../utils/docxPlainParagraphs';
import { buildDocxFooterWithPageNumbers } from '../../utils/docxFooter';

async function docxFieldParagraphs(
  text: string,
  options: DocxFieldParagraphOptions,
  useMathImages: boolean,
): Promise<Paragraph[]> {
  if (!useMathImages) {
    return createPlainMathParagraphs(text, options);
  }
  const { createMathParagraphs } = await import('../../utils/docxMath');
  return createMathParagraphs(text, options);
}

const normalizeLessonImplementationRows = (rows: any) => Array.isArray(rows)
  ? rows
      .filter((row) => row && typeof row === 'object')
      .map((row) => ({
        phase: String(row.phase ?? ''),
        teacherStudentActivity: Array.isArray(row.teacherStudentActivity) ? row.teacherStudentActivity.map(String) : [],
        expectedProduct: Array.isArray(row.expectedProduct) ? row.expectedProduct.map(String) : [],
      }))
  : [];

const buildDocxImplementationTable = async (rows: any[], useMath: boolean) => {
  const tableRows = [
    new TableRow({
      children: [
        new TableCell({
          width: { size: 50, type: WidthType.PERCENTAGE },
          children: [new Paragraph({ children: [new TextRun({ text: 'Hoạt động của GV - HS', bold: true })] })],
        }),
        new TableCell({
          width: { size: 50, type: WidthType.PERCENTAGE },
          children: [new Paragraph({ children: [new TextRun({ text: 'Dự kiến sản phẩm', bold: true })] })],
        }),
      ],
    }),
  ];

  for (const row of rows) {
    tableRows.push(new TableRow({
      children: [
        new TableCell({
          columnSpan: 2,
          children: [new Paragraph({ children: [new TextRun({ text: row.phase || 'Tiểu mục', bold: true })] })],
        }),
      ],
    }));

    const leftParagraphs = [];
    for (const item of row.teacherStudentActivity || []) {
      leftParagraphs.push(...await docxFieldParagraphs(`- ${item}`, {}, useMath));
    }

    const rightParagraphs = [];
    for (const item of row.expectedProduct || []) {
      rightParagraphs.push(...await docxFieldParagraphs(`- ${item}`, {}, useMath));
    }

    tableRows.push(new TableRow({
      children: [
        new TableCell({ verticalAlign: VerticalAlign.TOP, children: leftParagraphs.length ? leftParagraphs : [new Paragraph('')] }),
        new TableCell({ verticalAlign: VerticalAlign.TOP, children: rightParagraphs.length ? rightParagraphs : [new Paragraph('')] }),
      ],
    }));
  }

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: tableRows,
  });
};

interface DownloadButtonsProps {
  /** Xuất SKKN từ dữ liệu (PDF văn bản + DOCX có cấu trúc) — không dùng ảnh chụp HTML */
  skknExport?: {
    data: SKKNData;
    qualityShortLabel: string;
  };
  contentElementId?: string;
  title?: string;
  presentation?: Presentation | null;
  lessonPlan?: LessonPlan | null;
  quiz?: Quiz | null;
  homework?: Homework | null;
  activity?: Activity | null;
  video?: VideoResource | null;
  userSettings?: UserSettings;
  includeFooter?: boolean;
  images?: ImageResource[];
  isEditing?: boolean;
  onUpgradeSuccess?: (updatedPres: Presentation) => void;
  onShowToast?: (message: string, type: 'success' | 'error' | 'info') => void;
}

const downloadDocx = (doc: DocxDocument, filename: string) => {
    Packer.toBlob(doc).then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${filename}.docx`;
        a.click();
        URL.revokeObjectURL(url);
    });
};

const sanitizeDownloadFilename = (value: string | undefined | null, fallback = 'download') => {
    const sanitized = (value || '')
        .replace(/[<>:"/\\|?*\x00-\x1F]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/[. ]+$/g, '');

    return sanitized || fallback;
};

const toPlainExportText = (value: string | undefined | null) => {
    return mathToPlainText(value || '');
};

const createMatrixTable = async (matrix: MatrixRow[], useMathImages = true): Promise<Table> => {
    const headerRow1 = new TableRow({
        children: [
            new TableCell({ children: [new Paragraph({ text: "Nội dung / Chuẩn KT-KN", alignment: 'center' })], verticalAlign: VerticalAlign.CENTER, rowSpan: 2, width: { size: 25, type: WidthType.PERCENTAGE } }),
            ...COGNITIVE_LEVELS.map(level => new TableCell({ children: [new Paragraph({ text: level, alignment: 'center' })], columnSpan: 3, verticalAlign: VerticalAlign.CENTER })),
             new TableCell({ children: [new Paragraph({ text: "Tổng", alignment: 'center' })], verticalAlign: VerticalAlign.CENTER, rowSpan: 2 }),
        ],
    });
    const headerRow2 = new TableRow({
        children: COGNITIVE_LEVELS.flatMap(() => ['TNKQ', 'TL', 'Điểm'].map(text => new TableCell({ children: [new Paragraph({ text, alignment: 'center'})], verticalAlign: VerticalAlign.CENTER })))
    });

    const matrixRows: TableRow[] = [];
    for (const row of matrix) {
        const rowTotalPoints = COGNITIVE_LEVELS.reduce((sum, level) => sum + (row.levels?.[level]?.points || 0), 0);
        const topicParagraphs = await docxFieldParagraphs(row.topic || '', { textBold: true }, useMathImages);
        const standardsParagraphs = await docxFieldParagraphs(row.standards || '', {}, useMathImages);
        matrixRows.push(new TableRow({
            children: [
                new TableCell({ children: [...topicParagraphs, ...standardsParagraphs], verticalAlign: VerticalAlign.CENTER }),
                ...COGNITIVE_LEVELS.flatMap(level => {
                    const cell = row.levels?.[level] || { mcCount: 0, saCount: 0, points: 0 };
                    return [
                        new TableCell({ children: [new Paragraph({text: `${cell.mcCount}`, alignment: 'center'})], verticalAlign: VerticalAlign.CENTER }),
                        new TableCell({ children: [new Paragraph({text: `${cell.saCount}`, alignment: 'center'})], verticalAlign: VerticalAlign.CENTER }),
                        new TableCell({ children: [new Paragraph({text: `${cell.points}`, alignment: 'center'})], verticalAlign: VerticalAlign.CENTER }),
                    ];
                }),
                new TableCell({ children: [new Paragraph({text: `${rowTotalPoints.toFixed(2)}`, alignment: 'center'})], verticalAlign: VerticalAlign.CENTER }),
            ]
        }));
    }

    return new Table({
        rows: [headerRow1, headerRow2, ...matrixRows],
        width: { size: 100, type: WidthType.PERCENTAGE },
        // FIX: Replaced invalid 'inside' and 'outside' properties with correct ones
        borders: { top: { style: BorderStyle.SINGLE, size: 1, color: "auto" }, bottom: { style: BorderStyle.SINGLE, size: 1, color: "auto" }, left: { style: BorderStyle.SINGLE, size: 1, color: "auto" }, right: { style: BorderStyle.SINGLE, size: 1, color: "auto" }, insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "auto" }, insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "auto" } },
    });
}

const createGradingGuideTable = async (questions: Question[], useMathImages = true): Promise<(Paragraph | Table)[]> => {
    const mcQuestions = questions.filter(q => q.questionType === QuestionType.MultipleChoice);
    const saQuestions = questions.filter(q => q.questionType === QuestionType.ShortAnswer);
    
    const parseSubAnswers = (answer: string) => {
        const subAnswerRegex = /([a-d])[\)\.]\s*([ĐSđs])/gi;
        const matches = [...answer.matchAll(subAnswerRegex)];
        return matches.length > 0 ? matches.map(m => ({ label: m[1].toLowerCase(), value: m[2].toUpperCase() })) : null;
    };

    const saMatrixQuestions = saQuestions.filter(q => parseSubAnswers(q.correctAnswer) !== null);
    const saNormalQuestions = saQuestions.filter(q => parseSubAnswers(q.correctAnswer) === null);

    const elements: (Paragraph | Table)[] = [];

    if (mcQuestions.length > 0) {
        elements.push(new Paragraph({ text: "PHẦN I. ĐÁP ÁN TRẮC NGHIỆM NHIỀU LỰA CHỌN", heading: HeadingLevel.HEADING_2, spacing: { before: 200, after: 100 } }));
        elements.push(new Paragraph({ children: [new TextRun({ text: "(Mỗi câu trả lời đúng được 0,25 điểm)", italics: true })], spacing: { after: 200 } }));
        
        const colCount = 4;
        const rowCount = Math.ceil(mcQuestions.length / colCount);
        const tableRows: TableRow[] = [];
        
        const headerCells: TableCell[] = [];
        for (let i = 0; i < colCount; i++) {
            headerCells.push(new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Câu", bold: true })], alignment: 'center' })], shading: { fill: "F2F2F2" } }));
            headerCells.push(new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Đáp án", bold: true })], alignment: 'center' })], shading: { fill: "F2F2F2" } }));
        }
        tableRows.push(new TableRow({ children: headerCells }));

        for (let r = 0; r < rowCount; r++) {
            const cells: TableCell[] = [];
            for (let c = 0; c < colCount; c++) {
                const qIndex = r + c * rowCount;
                if (qIndex < mcQuestions.length) {
                    const q = mcQuestions[qIndex];
                    cells.push(new TableCell({ children: [new Paragraph({ text: String(questions.indexOf(q) + 1), alignment: 'center' })] }));
                    cells.push(new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: toPlainExportText(q.correctAnswer.replace(/^\s*[A-D]\s*[\.\)\-:]\s*/i, '').trim()), bold: true })], alignment: 'center' })] }));
                } else {
                    cells.push(new TableCell({ children: [new Paragraph("")] }));
                    cells.push(new TableCell({ children: [new Paragraph("")] }));
                }
            }
            tableRows.push(new TableRow({ children: cells }));
        }

        elements.push(new Table({
            rows: tableRows,
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: { top: { style: BorderStyle.SINGLE, size: 1 }, bottom: { style: BorderStyle.SINGLE, size: 1 }, left: { style: BorderStyle.SINGLE, size: 1 }, right: { style: BorderStyle.SINGLE, size: 1 }, insideHorizontal: { style: BorderStyle.SINGLE, size: 1 }, insideVertical: { style: BorderStyle.SINGLE, size: 1 } },
        }));
    }

    if (saMatrixQuestions.length > 0) {
        elements.push(new Paragraph({ text: "PHẦN II. ĐÁP ÁN TRẮC NGHIỆM ĐÚNG/SAI", heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 100 } }));
        elements.push(new Paragraph({ children: [new TextRun({ text: "(Đúng 1 ý được 0,1đ; đúng 2 ý được 0,25đ; đúng 3 ý được 0,5đ; đúng cả 4 ý được 1,0đ)", italics: true })], spacing: { after: 200 } }));

        saMatrixQuestions.forEach((q, i) => {
            const subAnswers = parseSubAnswers(q.correctAnswer);
            const tableRows = [
                new TableRow({
                    children: [
                        new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Lệnh hỏi", bold: true })], alignment: 'center' })], shading: { fill: "F2F2F2" } }),
                        new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Đáp án", bold: true })], alignment: 'center' })], shading: { fill: "F2F2F2" } }),
                    ]
                }),
                ...(subAnswers?.map(sa => new TableRow({
                    children: [
                        new TableCell({ children: [new Paragraph({ text: sa.label, alignment: 'center' })] }),
                        new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: sa.value, bold: true, color: sa.value === 'Đ' ? '008000' : 'FF0000' })], alignment: 'center' })] }),
                    ]
                })) || [])
            ];

            elements.push(new Paragraph({ children: [new TextRun({ text: `Câu ${questions.indexOf(q) + 1}`, bold: true })], spacing: { before: 200, after: 100 } }));
            elements.push(new Table({
                rows: tableRows,
                width: { size: 50, type: WidthType.PERCENTAGE }, // Smaller table for T/F
                borders: { top: { style: BorderStyle.SINGLE, size: 1 }, bottom: { style: BorderStyle.SINGLE, size: 1 }, left: { style: BorderStyle.SINGLE, size: 1 }, right: { style: BorderStyle.SINGLE, size: 1 }, insideHorizontal: { style: BorderStyle.SINGLE, size: 1 }, insideVertical: { style: BorderStyle.SINGLE, size: 1 } },
            }));
        });
    }

    if (saNormalQuestions.length > 0) {
        elements.push(new Paragraph({ text: "PHẦN III. HƯỚNG DẪN CHẤM TỰ LUẬN", heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 100 } }));
        const tableRows: TableRow[] = [
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Câu", bold: true })], alignment: 'center' })], shading: { fill: "F2F2F2" }, width: { size: 10, type: WidthType.PERCENTAGE } }),
                    new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Nội dung đáp án / Hướng dẫn giải", bold: true })], alignment: 'center' })], shading: { fill: "F2F2F2" }, width: { size: 80, type: WidthType.PERCENTAGE } }),
                    new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: "Điểm", bold: true })], alignment: 'center' })], shading: { fill: "F2F2F2" }, width: { size: 10, type: WidthType.PERCENTAGE } }),
                ]
            }),
        ];

        for (const q of saNormalQuestions) {
            const answerParagraphs = await docxFieldParagraphs(q.correctAnswer || '', {}, useMathImages);
            const explanationParagraphs = q.explanation
                ? await docxFieldParagraphs(q.explanation, { prefix: 'Giải thích: ', textItalics: true, spacingBefore: 100 }, useMathImages)
                : [];

            tableRows.push(new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph({ text: String(questions.indexOf(q) + 1), alignment: 'center' })] }),
                    new TableCell({ children: [...answerParagraphs, ...explanationParagraphs] }),
                    new TableCell({ children: [new Paragraph({ text: "---", alignment: 'center' })] }),
                ]
            }));
        }

        elements.push(new Table({
            rows: tableRows,
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: { top: { style: BorderStyle.SINGLE, size: 1 }, bottom: { style: BorderStyle.SINGLE, size: 1 }, left: { style: BorderStyle.SINGLE, size: 1 }, right: { style: BorderStyle.SINGLE, size: 1 }, insideHorizontal: { style: BorderStyle.SINGLE, size: 1 }, insideVertical: { style: BorderStyle.SINGLE, size: 1 } },
        }));
    }

    return elements;
};

const createSpecificationTables = async (specification: SpecificationTopic[], useMathImages = true): Promise<(Paragraph | Table)[]> => {
    const elements: (Paragraph | Table)[] = [];
    for (const topic of specification) {
        const items = Array.isArray(topic?.items) ? topic.items : [];
        const header = new TableRow({
            // FIX: Use TextRun for bold property
            children: ['Yêu cầu cần đạt', 'Mức độ', 'Hình thức', 'Số câu', 'Điểm', 'Ghi chú'].map(text => new TableCell({ children: [new Paragraph({ children: [new TextRun({ text, bold: true })], alignment: 'center' })], verticalAlign: VerticalAlign.CENTER })),
            tableHeader: true,
        });

        const rows: TableRow[] = [];
        for (const item of items) {
            rows.push(new TableRow({
                children: [
                    new TableCell({ children: await docxFieldParagraphs(item?.requirement || '', {}, useMathImages), verticalAlign: VerticalAlign.CENTER, width: { size: 35, type: WidthType.PERCENTAGE } }),
                    new TableCell({ children: await docxFieldParagraphs(item?.cognitiveLevel || '', {}, useMathImages), verticalAlign: VerticalAlign.CENTER }),
                    new TableCell({ children: await docxFieldParagraphs(item?.questionFormat || '', {}, useMathImages), verticalAlign: VerticalAlign.CENTER }),
                    new TableCell({ children: [new Paragraph({ text: String(item?.questionCount ?? ''), alignment: 'center' })], verticalAlign: VerticalAlign.CENTER }),
                    new TableCell({ children: [new Paragraph({ text: String(item?.points ?? ''), alignment: 'center' })], verticalAlign: VerticalAlign.CENTER }),
                    new TableCell({ children: await docxFieldParagraphs(item?.notes || '', {}, useMathImages), verticalAlign: VerticalAlign.CENTER }),
                ]
            }));
        }

        const table = new Table({
            rows: [header, ...rows],
            width: { size: 100, type: WidthType.PERCENTAGE },
            // FIX: Replaced invalid 'inside' and 'outside' properties with correct ones
            borders: { top: { style: BorderStyle.SINGLE, size: 1 }, bottom: { style: BorderStyle.SINGLE, size: 1 }, left: { style: BorderStyle.SINGLE, size: 1 }, right: { style: BorderStyle.SINGLE, size: 1 }, insideHorizontal: { style: BorderStyle.SINGLE, size: 1 }, insideVertical: { style: BorderStyle.SINGLE, size: 1 } },
        });

        elements.push(
            ...(await docxFieldParagraphs(topic?.topic || '', { textBold: true, spacingBefore: 200 }, useMathImages)),
            ...(await docxFieldParagraphs(topic?.standards || '', { textItalics: true, spacingAfter: 200 }, useMathImages)),
            table,
            new Paragraph({ text: "" }),
        );
    }
    return elements;
};

const DropdownItem: React.FC<{ onClick: () => void; children: React.ReactNode; disabled?: boolean }> = ({ onClick, children, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
  >
    {children}
  </button>
);


export const DownloadButtons: React.FC<DownloadButtonsProps> = ({ skknExport, contentElementId, title, presentation, lessonPlan, quiz, homework, activity, video, userSettings, includeFooter, images, isEditing, onUpgradeSuccess, onShowToast }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [html5ShareOpen, setHtml5ShareOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const rawTitle = title || presentation?.title || lessonPlan?.title || quiz?.title || homework?.title || activity?.title || video?.name || 'download';
  const finalTitle = sanitizeDownloadFilename(rawTitle, 'download');
  const canRemoveWatermark = checkCanRemoveWatermark(userSettings);

  const createDocxDocument = (children: (Paragraph | Table)[]) => {
    const footer = buildDocxFooterWithPageNumbers({ showWatermark: !canRemoveWatermark });
    return new DocxDocument({
      styles: {
        default: {
          document: {
            run: {
              size: 24, // 12pt
              font: "Times New Roman",
            },
          },
          heading1: {
            run: {
              size: 28, // 14pt
              bold: true,
              font: "Times New Roman",
            },
            paragraph: {
              spacing: { before: 240, after: 120 },
            },
          },
          heading2: {
            run: {
              size: 26, // 13pt
              bold: true,
              font: "Times New Roman",
            },
            paragraph: {
              spacing: { before: 200, after: 100 },
            },
          },
        },
      },
      sections: [{ children, footers: { default: footer } }],
    });
  };

  const applyPdfWatermark = (pdf: jsPDF) => {
    if (canRemoveWatermark) return;

    const pageCount = typeof (pdf as any).getNumberOfPages === 'function'
      ? (pdf as any).getNumberOfPages()
      : pdf.internal.getNumberOfPages();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    for (let page = 1; page <= pageCount; page++) {
      pdf.setPage(page);
      pdf.setFontSize(9);
      pdf.setTextColor(148, 163, 184);
      pdf.text('Powered by Bookai.vn', pageWidth - 12, pageHeight - 6, { align: 'right' });
    }
  };
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDownloadPdfGeneric = async () => {
    if (skknExport) {
      setIsDownloading(true);
      try {
        await exportSkknPdf({
          data: skknExport.data,
          qualityShortLabel: skknExport.qualityShortLabel,
          filename: finalTitle,
          applyWatermark: applyPdfWatermark,
        });
      } catch (error) {
        console.error('Error generating SKKN PDF:', error);
      } finally {
        setIsDownloading(false);
      }
      return;
    }
    setIsDownloading(true);
    const input = contentElementId ? document.getElementById(contentElementId) : null;
    if (!input) {
      if (lessonPlan) {
        await handleDownloadPdfLessonPlan(lessonPlan);
        return;
      }
      console.warn(
        'PDF dạng ảnh cần vùng nội dung đang hiển thị (mở tài liệu để sửa), hoặc dùng PDF từ máy chủ nếu có.'
      );
      setIsDownloading(false);
      return;
    }

    // Hide any elements that shouldn't be in the PDF (e.g., buttons, ad slots)
    const hiddenElements = input.querySelectorAll('button, .ad-slot, .no-pdf');
    const originalDisplays: string[] = [];
    hiddenElements.forEach(el => {
        originalDisplays.push((el as HTMLElement).style.display);
        (el as HTMLElement).style.display = 'none';
    });

    try {
        const canvas = await html2canvas(input, { 
            scale: 2,
            useCORS: true,
            logging: false,
            windowWidth: input.scrollWidth,
            windowHeight: input.scrollHeight
        });
        
        // Restore display
        hiddenElements.forEach((el, i) => (el as HTMLElement).style.display = originalDisplays[i]);

        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        
        const ratio = imgWidth / imgHeight;
        const canvasPdfHeight = pdfWidth / ratio;
        
        if (canvasPdfHeight > pdfHeight) {
            let heightLeft = imgHeight;
            let position = 0;
            const pageHeightInCanvas = (pdfHeight * imgWidth) / pdfWidth;
            
            const pageCanvas = document.createElement('canvas');
            pageCanvas.width = imgWidth;
            const pageCtx = pageCanvas.getContext('2d');
            
            let pageCount = 0;
            while(heightLeft > 0) {
                if (pageCount > 0) pdf.addPage();
                
                const sourceHeight = Math.min(pageHeightInCanvas, heightLeft);
                
                // Adjust pageCanvas height for the last chunk to avoid empty space
                pageCanvas.height = sourceHeight;
                
                // Clear and Draw the chunk
                pageCtx?.clearRect(0, 0, imgWidth, sourceHeight);
                pageCtx?.drawImage(
                    canvas, 
                    0, position, imgWidth, sourceHeight, 
                    0, 0, imgWidth, sourceHeight
                );
                
                const pageImgData = pageCanvas.toDataURL('image/png');
                
                // Calculate how much of the PDF page this chunk should cover
                const destHeight = (sourceHeight * pdfWidth) / imgWidth;
                pdf.addImage(pageImgData, 'PNG', 0, 0, pdfWidth, destHeight);
                
                heightLeft -= pageHeightInCanvas;
                position += pageHeightInCanvas;
                pageCount++;
            }
        } else {
            const imgData = canvas.toDataURL('image/png');
            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, canvasPdfHeight);
        }

        applyPdfWatermark(pdf);
        pdf.save(`${finalTitle}.pdf`);
    } catch (error) {
        console.error('Error generating PDF:', error);
        hiddenElements.forEach((el, i) => (el as HTMLElement).style.display = originalDisplays[i]);
    } finally {
        setIsDownloading(false);
    }
  };

  const handleDownloadDocxGeneric = async () => {
    if (skknExport) {
      setIsDownloading(true);
      try {
        await exportSkknDocx({
          data: skknExport.data,
          qualityShortLabel: skknExport.qualityShortLabel,
          filename: finalTitle,
          withFooterWatermark: !canRemoveWatermark,
        });
      } catch (error) {
        console.error('Error generating SKKN DOCX:', error);
      } finally {
        setIsDownloading(false);
      }
      return;
    }
    setIsDownloading(true);
    try {
        const element = contentElementId ? document.getElementById(contentElementId) : null;
        if (!element) {
            if (lessonPlan) {
                await handleDownloadDocxLessonPlan(lessonPlan);
                return;
            }
            console.warn(
                'DOCX từ giao diện cần mở tài liệu đang hiển thị nội dung, hoặc dùng mục xuất DOCX có sẵn theo cấu trúc dữ liệu.'
            );
            return;
        }
        const docChildren: Paragraph[] = [];
        
        for (const el of element.querySelectorAll('h1, h2, h3, h4, p, li, div[class*="font-bold"], div[class*="font-semibold"], span[class*="font-bold"], span[class*="font-semibold"]')) {
            const text = (el as HTMLElement).innerText;
            if (text && text.trim() && !el.closest('button') && el.closest('.fixed') === null) {
                const isHeading = el.tagName.startsWith('H') || el.classList.contains('font-bold') || el.classList.contains('font-semibold');
                if (isHeading || el.tagName === 'LI') {
                    docChildren.push(new Paragraph({
                        children: [new TextRun({ text, bold: isHeading })],
                        bullet: el.tagName === 'LI' ? { level: 0 } : undefined,
                        heading: isHeading ? HeadingLevel.HEADING_2 : undefined,
                        spacing: { after: 150 }
                    }));
                } else {
                    docChildren.push(...await docxFieldParagraphs(text, { spacingAfter: 150 }, true));
                }
            }
        }

        if (docChildren.length === 0 && element.innerText) {
            for (const line of element.innerText.split('\n')) {
                if (line.trim()) {
                    docChildren.push(...await docxFieldParagraphs(line, {}, true));
                }
            }
        }

        const doc = createDocxDocument(docChildren);

        const blob = await Packer.toBlob(doc);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${finalTitle}.docx`;
        a.click();
        URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error generating DOCX:', error);
    } finally {
        setIsDownloading(false);
    }
  };

  const handleDownloadDocxLessonPlan = async (plan: LessonPlan) => {
    setIsDownloading(true);
    const useMath = true;
    try {
        const docChildren: (Paragraph | Table)[] = [];

        docChildren.push(...await docxFieldParagraphs(plan.title || '', {
            alignment: AlignmentType.CENTER,
            textBold: true,
            spacingAfter: 200,
        }, useMath));

        const infoLineParts: string[] = [];
        if (plan.gradeLevel) infoLineParts.push(`Cấp học: ${plan.gradeLevel}`);
        const classLv = String(plan.classLevel ?? '').trim();
        if (classLv) infoLineParts.push(`Khối/Lớp: ${classLv}`);
        if (plan.subject) infoLineParts.push(`Môn: ${plan.subject}`);
        const book = String(plan.textbookSet ?? '').trim();
        if (book) infoLineParts.push(`Bộ sách: ${book}`);
        if (plan.model) infoLineParts.push(`Mô hình: ${plan.model}`);
        if (plan.teachingDuration != null && String(plan.teachingDuration).trim() !== '') {
            infoLineParts.push(`Thời lượng: ${plan.teachingDuration} phút`);
        }

        if (infoLineParts.length > 0) {
            docChildren.push(...await docxFieldParagraphs(infoLineParts.join('  |  '), {
                alignment: AlignmentType.CENTER,
                spacingAfter: 200,
            }, useMath));
        }
        docChildren.push(new Paragraph({ text: '' }));

        if (plan.model === TeachingModel.GDPT_2018_5512) {
            docChildren.push(new Paragraph({ text: 'I. MỤC TIÊU', heading: HeadingLevel.HEADING_1 }));
            docChildren.push(new Paragraph({ children: [new TextRun({ text: '1. Về kiến thức', bold: true })] }));
            docChildren.push(...await docxFieldParagraphs(plan.knowledgeObjective || '', { indentLeft: 720 }, useMath));

            docChildren.push(new Paragraph({ children: [new TextRun({ text: '2. Về năng lực', bold: true })] }));
            docChildren.push(...await docxFieldParagraphs(plan.competencyObjective || '', { indentLeft: 720 }, useMath));

            docChildren.push(new Paragraph({ children: [new TextRun({ text: '3. Về phẩm chất', bold: true })] }));
            docChildren.push(...await docxFieldParagraphs(plan.qualityObjective || '', { indentLeft: 720 }, useMath));

            docChildren.push(new Paragraph({ text: 'II. THIẾT BỊ DẠY HỌC VÀ HỌC LIỆU', heading: HeadingLevel.HEADING_1 }));
            docChildren.push(...await docxFieldParagraphs(plan.equipmentAndMaterials || '', {}, useMath));

            docChildren.push(new Paragraph({ text: 'III. TIẾN TRÌNH DẠY HỌC', heading: HeadingLevel.HEADING_1 }));
        }

        for (const step of plan.steps || []) {
            docChildren.push(new Paragraph({ text: step.step || 'Bước', heading: HeadingLevel.HEADING_2 }));
            const dur = String(step.duration ?? '').trim();
            if (dur) {
                docChildren.push(...await docxFieldParagraphs(`Thời lượng dự kiến: ${dur}`, { textItalics: true, spacingAfter: 120 }, useMath));
            }

            if (plan.model === TeachingModel.GDPT_2018_5512) {
                docChildren.push(new Paragraph({ children: [new TextRun({ text: 'a) Mục tiêu', bold: true })] }));
                docChildren.push(...await docxFieldParagraphs(step.objective || '', { indentLeft: 720 }, useMath));
                docChildren.push(new Paragraph({ children: [new TextRun({ text: 'b) Nội dung', bold: true })] }));
                docChildren.push(...await docxFieldParagraphs(step.content || '', { indentLeft: 720 }, useMath));
                docChildren.push(new Paragraph({ children: [new TextRun({ text: 'c) Sản phẩm', bold: true })] }));
                docChildren.push(...await docxFieldParagraphs(step.product || '', { indentLeft: 720 }, useMath));
                docChildren.push(new Paragraph({ children: [new TextRun({ text: 'd) Tổ chức thực hiện', bold: true })] }));
                const implementationRows = normalizeLessonImplementationRows(step.implementationRows);
                if (implementationRows.length > 0) {
                    docChildren.push(await buildDocxImplementationTable(implementationRows, useMath));
                } else {
                    docChildren.push(...await docxFieldParagraphs(step.implementation || '', { indentLeft: 720 }, useMath));
                }
            } else {
                docChildren.push(new Paragraph({ children: [new TextRun({ text: 'Hoạt động của giáo viên', bold: true })] }));
                docChildren.push(...await docxFieldParagraphs(step.teacherActivity || '', { indentLeft: 720 }, useMath));
                docChildren.push(new Paragraph({ children: [new TextRun({ text: 'Hoạt động của học sinh', bold: true })] }));
                docChildren.push(...await docxFieldParagraphs(step.studentActivity || '', { indentLeft: 720 }, useMath));
                docChildren.push(new Paragraph({ children: [new TextRun({ text: 'Đồ dùng dạy học', bold: true })] }));
                docChildren.push(...await docxFieldParagraphs(step.teachingAids || '', { indentLeft: 720 }, useMath));
                if (Array.isArray(step.competencyGoals) && step.competencyGoals.length > 0) {
                    docChildren.push(new Paragraph({ children: [new TextRun({ text: 'Mục tiêu năng lực', bold: true })] }));
                    for (const g of step.competencyGoals) {
                        docChildren.push(...await docxFieldParagraphs(`✓ ${g}`, { indentLeft: 720 }, useMath));
                    }
                }
            }
        }

        const doc = createDocxDocument(docChildren);
        const blob = await Packer.toBlob(doc);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${finalTitle}.docx`;
        a.click();
        URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error generating DOCX for lesson plan:', error);
    } finally {
        setIsDownloading(false);
    }
  };

  const handleDownloadPdfLessonPlan = async (plan: LessonPlan) => {
    setIsDownloading(true);
    try {
        const doc = new jsPDF('p', 'mm', 'a4');
        const pdfFontFamily = (await embedInterFontForPdf(doc)) ? 'Inter' : 'helvetica';

        let yPos = 20;
        const margin = 15;
        const maxWidth = doc.internal.pageSize.getWidth() - margin * 2;

        const addText = (text: string, size: number, isBold: boolean, indent = 0) => {
            if (!text) return;
            doc.setFontSize(size);
            doc.setFont(pdfFontFamily, isBold ? 'bold' : 'normal');
            
            const lines: string[] = doc.splitTextToSize(text, maxWidth - indent);
            const lineHeight = size * 0.3527; // convert pt to mm (1pt = 0.3527mm)
            const spacing = lineHeight * 0.5; // Half of line height for spacing

            lines.forEach((line) => {
                if (yPos + lineHeight > doc.internal.pageSize.getHeight() - margin) {
                    doc.addPage();
                    yPos = margin;
                    doc.setFont(pdfFontFamily, isBold ? 'bold' : 'normal');
                    doc.setFontSize(size);
                }
                doc.text(line, margin + indent, yPos);
                yPos += lineHeight + (spacing / lines.length); // Adjust yPos per line
            });
            yPos += spacing; // Extra space between paragraphs
        };

        const addImplementationRows = (rows: any[]) => {
            const pageHeight = doc.internal.pageSize.getHeight();
            const colGap = 4;
            const colWidth = (maxWidth - colGap) / 2;
            const lineHeight = 4.6;

            rows.forEach((row) => {
                addText(row.phase || 'Tiểu mục', 11, true, 5);

                const leftLines = doc.splitTextToSize((row.teacherStudentActivity || []).map((item: string) => `- ${item}`).join('\n'), colWidth - 4);
                const rightLines = doc.splitTextToSize((row.expectedProduct || []).map((item: string) => `- ${item}`).join('\n'), colWidth - 4);
                const rowHeight = Math.max(leftLines.length, rightLines.length) * lineHeight + 10;

                if (yPos + rowHeight > pageHeight - margin) {
                    doc.addPage();
                    yPos = margin;
                }

                const startY = yPos;
                doc.setDrawColor(220, 220, 220);
                doc.rect(margin + 5, startY, colWidth, rowHeight);
                doc.rect(margin + 5 + colWidth + colGap, startY, colWidth, rowHeight);

                doc.setFontSize(9);
                doc.setFont(pdfFontFamily, 'bold');
                doc.text('Hoạt động của GV - HS', margin + 7, startY + 5);
                doc.text('Dự kiến sản phẩm', margin + 7 + colWidth + colGap, startY + 5);

                doc.setFont(pdfFontFamily, 'normal');
                doc.setFontSize(9);
                leftLines.forEach((line: string, lineIndex: number) => {
                    doc.text(line, margin + 7, startY + 10 + lineIndex * lineHeight);
                });
                rightLines.forEach((line: string, lineIndex: number) => {
                    doc.text(line, margin + 7 + colWidth + colGap, startY + 10 + lineIndex * lineHeight);
                });

                yPos += rowHeight + 3;
            });
        };

        addText(plan.title, 18, true, 0);
        
        const infoParts = [];
        if (plan.gradeLevel) infoParts.push(`Cấp học: ${plan.gradeLevel}`);
        if (plan.classLevel) infoParts.push(`Lớp: ${plan.classLevel}`);
        if (plan.subject) infoParts.push(`Môn: ${plan.subject}`);
        if (plan.textbookSet) infoParts.push(`Bộ sách: ${plan.textbookSet}`);
        
        if (infoParts.length > 0) {
            addText(infoParts.join(' - '), 10, false, 0);
        }
        yPos += 5;

        if (plan.model === TeachingModel.GDPT_2018_5512) {
            addText("I. MỤC TIÊU", 14, true);
            addText("1. Về kiến thức", 12, true, 5);
            addText(plan.knowledgeObjective || "", 11, false, 10);
            addText("2. Về năng lực", 12, true, 5);
            addText(plan.competencyObjective || "", 11, false, 10);
            addText("3. Về phẩm chất", 12, true, 5);
            addText(plan.qualityObjective || "", 11, false, 10);
            yPos += 5;

            addText("II. THIẾT BỊ DẠY HỌC VÀ HỌC LIỆU", 14, true);
            addText(plan.equipmentAndMaterials || "", 11, false, 5);
            yPos += 5;

            addText("III. TIẾN TRÌNH DẠY HỌC", 14, true);
        }
        
        (plan.steps || []).forEach(step => {
            yPos += 3;
            addText(step.step || "Bước", 12, true);
            if (plan.model === TeachingModel.GDPT_2018_5512) {
                addText(`a) Mục tiêu: ${step.objective || ''}`, 11, false, 5);
                addText(`b) Nội dung:`, 11, true, 5);
                addText(step.content || '', 11, false, 10);
                addText(`c) Sản phẩm: ${step.product || ''}`, 11, false, 5);
                addText(`d) Tổ chức thực hiện:`, 11, true, 5);
                const implementationRows = normalizeLessonImplementationRows(step.implementationRows);
                if (implementationRows.length > 0) {
                    addImplementationRows(implementationRows);
                } else {
                    addText(step.implementation || '', 11, false, 10);
                }
            } else {
                addText("Hoạt động của giáo viên:", 11, true, 5);
                addText(step.teacherActivity || '', 11, false, 10);
                addText("Hoạt động của học sinh:", 11, true, 5);
                addText(step.studentActivity || '', 11, false, 10);
            }
        });

        applyPdfWatermark(doc);
        doc.save(`${finalTitle}.pdf`);
    } catch (error) {
        console.error('Error generating PDF for lesson plan:', error);
    } finally {
        setIsDownloading(false);
    }
  };

  const handleDownloadPptxScreenshots = async (presData: Presentation) => {
    if (!presData) return;
    
    const publicHtml5Url = (presData as any).publicHtml5Url ?? (presData as any).public_html5_url ?? null;
    const shareToken = (presData as any).shareToken ?? (presData as any).share_token ?? '';

    if (!publicHtml5Url) {
        onShowToast?.("Vui lòng tạo bản xem trình chiếu (Nâng cấp HTML5) trước khi tải bản chụp.", "error");
        return;
    }

    onShowToast?.("Đang chuẩn bị bản trình chiếu HTML5 để chụp ảnh chất lượng cao...", "info");
    
    // Tạo link có kèm action tự động tải
    const downloadUrl = new URL(publicHtml5Url, window.location.origin);
    if (shareToken) downloadUrl.searchParams.set('token', shareToken);
    downloadUrl.searchParams.set('action', 'download-pptx');

    // Mở tab mới để thực hiện quy trình chụp ảnh chuyên nghiệp
    window.open(downloadUrl.toString(), '_blank');
  };

  const handleDownloadPptx = async (presData: Presentation) => {
    setIsDownloading(true);
    // Helper function for footer text (moved up to avoid ReferenceError)
    const getFooterText = (alignment: 'left' | 'right') => {
      if (!includeFooter || !userSettings) return '';
      return userSettings.footerSettings
        .filter(item => item.include && item.alignment === alignment)
        .map(item => userSettings[item.key as keyof UserSettings])
        .filter(value => typeof value === 'string' && value)
        .join(' | ');
    };
    const fullFooter = [getFooterText('left'), getFooterText('right')].filter(Boolean).join(' | ');

    try {
        // --- CHẾ ĐỘ CHUYÊN NGHIỆP: Xuất qua Backend ---
        // Ưu tiên xuất qua backend nếu là bản trình chiếu (Presentation)
        // để đảm bảo chất lượng thiết kế cao cấp nhất.
        if (presData && presData.slides) {
            try {
                // Giải mã theme nếu nó đang ở dạng string (JSON) để bổ sung footer
                let themeObj = presData.theme;
                if (typeof themeObj === 'string' && themeObj.startsWith('{')) {
                    try {
                        themeObj = JSON.parse(themeObj);
                    } catch (e) {}
                }

                // Bổ sung footer cho backend export
                // Loại bỏ nested presentation nếu có để tránh payload quá lớn
                const { presentation: _, ...cleanPresData } = presData as any;
                const slidesWithImageUrls = Array.isArray(cleanPresData.slides)
                  ? cleanPresData.slides.map((slide: Slide) => {
                      const cid = (slide.content as any)?.imageId;
                      if (cid == null || cid === '') return slide;
                      const img = images?.find((i) => String(i.id) === String(cid));
                      const rawUrl = (slide as any).imageUrl || img?.url;
                      if (!rawUrl || typeof rawUrl !== 'string') return slide;
                      return { ...slide, imageUrl: formatAssetUrl(rawUrl) };
                    })
                  : cleanPresData.slides;
                const exportPayload = {
                  ...cleanPresData,
                  slides: slidesWithImageUrls,
                  theme: themeObj,
                  footer: fullFooter,
                  readabilityMode: {
                    highContrast: true
                  }
                };
                
                const blob = await exportSlidesToPptx(exportPayload as any);
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${sanitizeDownloadFilename(presData.title, 'Presentation')}.pptx`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                return;
            } catch (err) {
                console.error("Backend export failed, falling back to frontend:", err);
                // Nếu lỗi backend, fallback về xuất frontend cơ bản bên dưới
            }
        }

        const pptx = new PptxGenJS();
        
        pptx.layout = 'LAYOUT_WIDE';
        let fallbackTheme = presData.theme as any;
        if (typeof fallbackTheme === 'string' && fallbackTheme.startsWith('{')) {
            try {
                fallbackTheme = JSON.parse(fallbackTheme);
            } catch (e) {}
        }
        const fallbackPrimaryColor = String(fallbackTheme?.primaryColor || '0F766E').replace('#', '');
        const fallbackBackgroundColor = 'F8FAFC';
        const fallbackTitleColor = '111827';
        const fallbackBodyColor = '1F2937';
        const fallbackFooterColor = '475569';
        
        // --- OPTIMIZATION: Batch fetch image base64 data ---
        const imageUrlsToFetch: string[] = [];
        if (Array.isArray(presData.slides)) {
            presData.slides.forEach(slide => {
                const slideImage = images?.find(img => img.id === slide.content.imageId);
                if (slideImage?.url && !slideImage.url.startsWith('data:image')) {
                    imageUrlsToFetch.push(slideImage.url);
                }
            });
        }
        
        // Remove duplicates
        const uniqueUrls = Array.from(new Set(imageUrlsToFetch));
        const imageBase64Cache = await fetchImagesAsBase64Batch(uniqueUrls);
        // --------------------------------------------------

        const getSlideTextContent = (slide: Slide) => {
            const content = slide.content as any;
            if (content.explanation) return { text: content.explanation, bullet: false };
            if (content.text) return { text: content.text, bullet: false };
            const listKeys = ['agendaPoints', 'summaryPoints', 'mainPoints', 'options', 'discussionPrompts', 'bullets'];
            for (const key of listKeys) {
                if (Array.isArray(content[key]) && content[key]!.length > 0) {
                    return { text: (content[key] as string[]).join('\n'), bullet: true };
                }
            }
            if (content.question) return { text: content.question, bullet: false };
            return { text: '', bullet: false };
        };

        if (Array.isArray(presData.slides)) {
            for (const slide of presData.slides) {
                const pptxSlide = pptx.addSlide();
                pptxSlide.background = { color: fallbackBackgroundColor };
                
                const slideImage = images?.find(img => img.id === slide.content.imageId);
                let imageUrl = (slide as any).imageUrl || '';
                if (slideImage?.url) {
                    if (slideImage.url.startsWith('data:image')) {
                        imageUrl = slideImage.url;
                    } else {
                        imageUrl = imageBase64Cache[slideImage.url] || '';
                    }
                }
                const hasImage = imageUrl && imageUrl.startsWith('data:image');

                const { text: slideText, bullet: isBullet } = getSlideTextContent(slide);
                const textLength = slideText.length;
                
                // Add Title
                pptxSlide.addText(slide.title, { 
                    x: '5%', y: '5%', w: '90%', h: '15%', 
                    fontSize: 28, bold: true, align: 'center', valign: 'middle',
                    color: fallbackTitleColor,
                    margin: 0
                });
                pptxSlide.addText(' ', {
                    x: '38%', y: '16.5%', w: '24%', h: 0.04,
                    color: fallbackPrimaryColor,
                    fill: { color: fallbackPrimaryColor },
                    line: { color: fallbackPrimaryColor }
                });

                // Add Content based on layout
                if (hasImage) {
                    const isTextHeavy = textLength > 250;
                    
                    const textOptions: PptxGenJS.TextPropsOptions = {
                        x: '5%', y: '25%', h: '65%', 
                        fontSize: 18, valign: 'top', 
                        bullet: isBullet ? { type: 'bullet' } : false,
                        color: fallbackBodyColor,
                        margin: 0.08,
                    };
                    
                    const imageOptions: PptxGenJS.ImageProps = {
                        data: imageUrl,
                        y: '25%',
                    };

                    if (isTextHeavy) { // Text takes more space
                        textOptions.w = '55%';
                        imageOptions.x = '62.5%';
                        imageOptions.w = '32.5%';
                        imageOptions.h = '55%';
                    } else { // Balanced or image-heavy
                        textOptions.w = '45%';
                        imageOptions.x = '52.5%';
                        imageOptions.w = '42.5%';
                        imageOptions.h = '65%';
                    }
                    
                    pptxSlide.addText(slideText, textOptions);
                    pptxSlide.addImage(imageOptions);

                } else { // Text only
                     pptxSlide.addText(slideText, { 
                        x: '10%', y: '25%', w: '80%', h: '65%', 
                        fontSize: 20, valign: 'top',
                        bullet: isBullet ? { type: 'bullet' } : false,
                        color: fallbackBodyColor,
                        margin: 0.08,
                    });
                }

                // Add Footer
                const leftFooter = getFooterText('left');
                const rightFooter = getFooterText('right');
                if (leftFooter) pptxSlide.addText(leftFooter, { x: 0.5, y: '92%', w: '45%', h: 0.25, fontSize: 10, align: 'left', color: fallbackFooterColor });
                if (rightFooter) pptxSlide.addText(rightFooter, { x: 7.83, y: '92%', w: '45%', h: 0.25, fontSize: 10, align: 'right', color: fallbackFooterColor });
                if (!canRemoveWatermark) {
                    pptxSlide.addText('Powered by Bookai.vn', {
                        x: 7.1,
                        y: '95%',
                        w: 2.2,
                        h: 0.2,
                        fontSize: 9,
                        italic: true,
                        align: 'right',
                        color: '94A3B8',
                    });
                }
            }
        }
        
        await pptx.writeFile({ fileName: `${finalTitle}.pptx` });
    } catch (error) {
        console.error('Error generating PPTX:', error);
    } finally {
        setIsDownloading(false);
    }
  };
  
  const createQuizDocx = async (
    quizData: Quiz,
    includeAnswers: boolean,
    forStudent: boolean,
    useMathImages = true,
  ): Promise<(Paragraph | Table)[]> => {
    const schoolName = userSettings?.schoolName || "[Tên trường]";
    const subject = quizData.subject || "[Môn học]";

    const header = [
        // FIX: Use TextRun for bold property
        new Paragraph({ children: [new TextRun({ text: schoolName, bold: true })], alignment: 'center' }),
        new Paragraph({ children: [new TextRun({ text: `BÀI KIỂM TRA ${quizData.category?.toUpperCase() || ''}`, bold: true })], heading: HeadingLevel.HEADING_1, alignment: 'center' }),
        new Paragraph({ 
            children: [
                new TextRun({ 
                    text: `Môn: ${subject}${quizData.classLevel ? ` - Lớp: ${quizData.classLevel}` : ` - Cấp học: ${quizData.gradeLevel}`}${quizData.textbookSet ? ` - Bộ sách: ${quizData.textbookSet}` : ''}\nThời gian làm bài: ${quizData.duration} phút`, 
                    bold: true 
                })
            ], 
            alignment: 'center' 
        }),
        new Paragraph({ text: '' }),
    ];

    const studentInfo = forStudent ? [
        new Paragraph({ text: "Họ và tên: ...................................................................." }),
        new Paragraph({ text: "Lớp: ............................................................................" }),
        new Paragraph({ text: '' }),
    ] : [];

    const questionsContent: (Paragraph | Table)[] = [];
    for (const [index, q] of (quizData.questions || []).entries()) {
        questionsContent.push(...await docxFieldParagraphs(q.questionText || '', {
            prefix: `Câu ${index + 1}: `,
            prefixBold: true,
        }, useMathImages));
        if (q.questionType === QuestionType.MultipleChoice && q.options) {
            for (const [optIndex, opt] of q.options.entries()) {
                questionsContent.push(...await docxFieldParagraphs(opt || '', {
                    prefix: `${String.fromCharCode(65 + optIndex)}. `,
                    indentLeft: 720,
                }, useMathImages));
            }
        }
        if (forStudent && q.questionType === QuestionType.ShortAnswer) {
             questionsContent.push(new Paragraph({text: '\n\n\n'}));
        }
        if (includeAnswers) {
            questionsContent.push(...await docxFieldParagraphs(q.correctAnswer || '', {
                prefix: 'Đáp án: ',
                prefixBold: true,
                textColor: '008000',
                spacingBefore: 100,
            }, useMathImages));
            questionsContent.push(...await docxFieldParagraphs(q.explanation || '', {
                prefix: 'Giải thích: ',
                textItalics: true,
            }, useMathImages));
        }
        questionsContent.push(new Paragraph({ text: '' }));
    }

    const rubric = (includeAnswers && quizData.gradingGuide) ? [
        new Paragraph({ text: "HƯỚNG DẪN CHẤM & RUBRIC", heading: HeadingLevel.HEADING_1, pageBreakBefore: true }),
        ...(await createGradingGuideTable(quizData.questions || [], useMathImages)),
        new Paragraph({ text: "QUY TẮC CHUNG & GHI CHÚ", heading: HeadingLevel.HEADING_2, spacing: { before: 400, after: 100 } }),
        ...(await docxFieldParagraphs(quizData.gradingGuide || '', {}, useMathImages))
    ] : [];
    
    const footer = forStudent ? [
        new Paragraph({ text: '' }),
        new Paragraph({ children: [new TextRun(`....................Hết....................`)], alignment: 'center' }),
        new Paragraph({ children: [new TextRun(`Người ra đề: ${userSettings?.teacherName || '[Tên giáo viên]'}`)], alignment: 'right' }),
    ] : [];

    return [...header, ...studentInfo, ...questionsContent, ...rubric, ...footer];
  };

  const handleDownloadQuizDocx = async (
    quizData: Quiz,
    includeAnswers: boolean,
    forStudent: boolean,
    filenameSuffix: string,
    useMathImages = true,
  ) => {
    setIsDownloading(true);
    try {
      const children = await createQuizDocx(quizData, includeAnswers, forStudent, useMathImages);
      const doc = createDocxDocument(children);
      downloadDocx(doc, `${finalTitle}${filenameSuffix}`);
    } catch (error) {
      console.error('Error generating quiz DOCX:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadTestRecord = async (quizData: Quiz) => {
      if (!quizData.matrix || !userSettings) return;

      const schoolName = userSettings.schoolName || '[Tên trường]';
      const subject = quizData.subject || '[Môn học]';

      const header = new Paragraph({
          // FIX: Use TextRun for bold property
          children: [ new TextRun({ text: `${schoolName.toUpperCase()}\nTổ: ${subject}`, bold: true }) ],
          alignment: 'center',
      });

      const title = new Paragraph({ text: "BIÊN BẢN RA ĐỀ KIỂM TRA", heading: HeadingLevel.HEADING_1, alignment: 'center', spacing: { before: 300, after: 300 } });
      const matrixTable = await createMatrixTable(quizData.matrix, true);
      const signature = new Paragraph({
          children: [
              new TextRun("\t\t\t\t\t\t"),
              new TextRun(`Ngày ..... tháng ..... năm .....\n`),
              // FIX: Use TextRun for bold property
              new TextRun({ text: "\t\t\t\t\t\tNgười ra đề\n\n\n\n", bold: true }),
              new TextRun(userSettings.teacherName)
          ],
          alignment: 'right',
          spacing: { before: 800 }
      });

      const doc = createDocxDocument([header, title, new Paragraph(`Môn: ${subject}\t\tLớp: ${quizData.gradeLevel}`), new Paragraph(`Thời gian: ${quizData.duration} phút`), new Paragraph({text:''}), matrixTable, signature]);

      downloadDocx(doc, `${finalTitle}_BienBan`);
  };
  
    const handleDownloadMatrixOnly = async (quizData: Quiz) => {
        if (!quizData.matrix) { alert("Không có dữ liệu ma trận để tải xuống."); return; }
        const title = new Paragraph({ text: "MA TRẬN ĐỀ KIỂM TRA", heading: HeadingLevel.HEADING_1, alignment: 'center', spacing: { after: 300 } });
        // FIX: Use TextRun for bold property
        const quizTitle = new Paragraph({ children: [new TextRun({ text: quizData.title.toUpperCase(), bold: true })], alignment: 'center', spacing: { after: 300 } });
        const matrixTable = await createMatrixTable(quizData.matrix, true);
        const doc = createDocxDocument([title, quizTitle, new Paragraph({text:''}), matrixTable]);
        downloadDocx(doc, `${finalTitle}_MaTran`);
    };

    const handleDownloadSpecification = async (quizData: Quiz) => {
        if (!quizData.specification) { alert("Không có dữ liệu bản đặc tả để tải."); return; }
        const doc = createDocxDocument([ new Paragraph({ text: "BẢN ĐẶC TẢ KỸ THUẬT CHI TIẾT", heading: HeadingLevel.HEADING_1, alignment: 'center' }), new Paragraph({ children: [new TextRun({ text: quizData.title, bold: true })], alignment: 'center', spacing: { after: 400 } }), ...(await createSpecificationTables(quizData.specification, true)), ]);
        downloadDocx(doc, `${finalTitle}_BanDacTa`);
    };

    const handleDownloadAll = async (quizData: Quiz, useMathImages = true) => {
        setIsDownloading(true);
        try {
        let allChildren: (Paragraph | Table)[] = [];
        allChildren.push(new Paragraph({ text: "PHẦN 1: ĐỀ THI", heading: HeadingLevel.HEADING_1, pageBreakBefore: false }));
        allChildren.push(...await createQuizDocx(quizData, false, true, useMathImages));
        
        allChildren.push(new Paragraph({ text: "PHẦN 2: ĐÁP ÁN & HƯỚNG DẪN CHẤM", heading: HeadingLevel.HEADING_1, pageBreakBefore: true }));
        allChildren.push(...(await createQuizDocx(quizData, true, false, useMathImages)).slice(4)); // Slice to remove header

        if (quizData.matrix?.length) {
            allChildren.push(new Paragraph({ text: "PHẦN 3: MA TRẬN ĐỀ", heading: HeadingLevel.HEADING_1, pageBreakBefore: true }));
            allChildren.push(await createMatrixTable(quizData.matrix, useMathImages));
        }
        if (quizData.specification?.length) {
            allChildren.push(new Paragraph({ text: "PHẦN 4: BẢN ĐẶC TẢ", heading: HeadingLevel.HEADING_1, pageBreakBefore: true }));
            allChildren.push(...(await createSpecificationTables(quizData.specification, useMathImages)));
        }
        const doc = createDocxDocument(allChildren);
        downloadDocx(doc, `${finalTitle}_ToanBo`);
        } catch (error) {
            console.error('Error generating complete quiz DOCX:', error);
        } finally {
            setIsDownloading(false);
        }
    };


  const handleDownloadGradingSheet = (quizData: Quiz) => {
    const questions = quizData.questions || [];
    const headerCells = [
        new TableCell({ children: [new Paragraph({ text: "STT", alignment: 'center'})]}),
        new TableCell({ children: [new Paragraph({ text: "Họ và tên", alignment: 'center'})], width: {size: 30, type: WidthType.PERCENTAGE}}),
        ...questions.map((q, i) => new TableCell({ children: [new Paragraph({ text: `Câu ${i+1}`, alignment: 'center'})]})),
        new TableCell({ children: [new Paragraph({ text: "Tổng điểm", alignment: 'center'})]}),
    ];
    const header = new TableRow({ children: headerCells, tableHeader: true });

    const emptyRows = Array(40).fill(0).map((_, index) => new TableRow({
        children: [
            new TableCell({ children: [new Paragraph(`${index + 1}`)]}),
            new TableCell({ children: [new Paragraph("")]}),
            ...questions.map(() => new TableCell({ children: [new Paragraph("")]})),
            new TableCell({ children: [new Paragraph("")]}),
        ]
    }));
    
    const table = new Table({
        rows: [header, ...emptyRows],
        width: { size: 100, type: WidthType.PERCENTAGE },
        // FIX: Replaced invalid 'inside' and 'outside' properties with correct ones
        borders: { top: { style: BorderStyle.SINGLE, size: 1 }, bottom: { style: BorderStyle.SINGLE, size: 1 }, left: { style: BorderStyle.SINGLE, size: 1 }, right: { style: BorderStyle.SINGLE, size: 1 }, insideHorizontal: { style: BorderStyle.SINGLE, size: 1 }, insideVertical: { style: BorderStyle.SINGLE, size: 1 } },
    });
    
    const doc = createDocxDocument([
        new Paragraph({ text: "PHIẾU CHẤM BÀI", heading: HeadingLevel.HEADING_1, alignment: 'center'}),
        new Paragraph({ text: `Bài kiểm tra: ${quizData.title}`, alignment: 'center'}),
        new Paragraph({text:''}),
        table
    ]);
    downloadDocx(doc, `${finalTitle}_PhieuCham`);
  };
  
  if (video) {
    return (
      <button 
        onClick={async () => {
          if (!video.uri) return;
          setIsDownloading(true);
          try {
            const downloadPath = formatAssetUrl(getVideoDownloadUrl(video));
            const filename = `${video.name.replace(/\s/g, '_')}.mp4`;
            
            // Nếu là link nội bộ (path bắt đầu bằng /api/edu)
            if (downloadPath.startsWith('/api/edu')) {
                await downloadFromApi(downloadPath, filename);
                return;
            }

            // Fallback cho link ngoài
            const response = await fetch(downloadPath);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
          } catch (e) {
            console.error("Video download failed", e);
          } finally {
            setIsDownloading(false);
          }
        }} 
        disabled={isDownloading || !video.uri}
        title="Tải xuống Video MP4" 
        className="flex items-center justify-center space-x-2 font-medium px-5 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors disabled:bg-teal-400 disabled:cursor-not-allowed"
      >
        {isDownloading ? <Spinner size="sm" /> : <ArrowDownTrayIcon className="h-5 w-5" />}
        <span>Tải MP4</span>
      </button>
    );
  }

  if (homework) {
    return (
        <div className="relative" ref={dropdownRef}>
            <button 
                onClick={() => setIsOpen(p => !p)} 
                disabled={isDownloading}
                className="flex items-center justify-center space-x-2 font-medium px-5 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors disabled:bg-teal-400 disabled:cursor-not-allowed"
            >
                {isDownloading ? <Spinner size="sm" /> : <ArrowDownTrayIcon className="h-5 w-5" />}
                <span>Tải xuống</span>
                <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && !isDownloading && (
                <div className="absolute bottom-full mb-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1">
                        <DropdownItem onClick={async () => { 
                            setIsOpen(false); 
                            if (APP_MODE === 'PRODUCTION' && homework.id && !homework.id.toString().startsWith('id_')) {
                                setIsDownloading(true);
                                try {
                                    await downloadPdfFromBackend('homework', homework.id, `${homework.title}.pdf`);
                                } catch (e) {
                                    handleDownloadPdfGeneric();
                                } finally {
                                    setIsDownloading(false);
                                }
                            } else {
                                handleDownloadPdfGeneric(); 
                            }
                        }}>Tải Bài tập (PDF)</DropdownItem>
                        {contentElementId ? (
                        <DropdownItem onClick={() => { setIsOpen(false); handleDownloadPdfGeneric(); }}>Tải Bài tập (PDF - Ảnh)</DropdownItem>
                        ) : null}
                        <DropdownItem onClick={() => { setIsOpen(false); handleDownloadDocxGeneric(); }}>Tải Bài tập (DOCX)</DropdownItem>
                    </div>
                </div>
            )}
        </div>
    );
  }

  if (quiz && userSettings) {
    return (
       <div className="relative" ref={dropdownRef}>
            <button 
                onClick={() => setIsOpen(p => !p)} 
                disabled={isDownloading}
                className="flex items-center justify-center space-x-2 font-medium px-5 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:bg-indigo-400 disabled:cursor-not-allowed"
            >
                {isDownloading ? <Spinner size="sm" /> : <ArrowDownTrayIcon className="h-5 w-5" />}
                <span>Tải xuống</span>
                <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && !isDownloading && (
                <div className="absolute bottom-full mb-2 w-72 max-w-[90vw] rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1">
                        <DropdownItem onClick={async () => {
                            setIsOpen(false);
                            if (APP_MODE === 'PRODUCTION' && quiz.id && !quiz.id.toString().startsWith('id_')) {
                                setIsDownloading(true);
                                try {
                                    await downloadDocxFromBackend('quiz', quiz.id, `${finalTitle}_DeThi.docx`, { variant: 'student' });
                                } catch {
                                    await handleDownloadQuizDocx(quiz, false, true, '_DeThi');
                                } finally {
                                    setIsDownloading(false);
                                }
                            } else {
                                await handleDownloadQuizDocx(quiz, false, true, '_DeThi');
                            }
                        }}>Đề thi (Word — chỉnh sửa)</DropdownItem>
                        <DropdownItem onClick={async () => {
                            setIsOpen(false);
                            if (APP_MODE === 'PRODUCTION' && quiz.id && !quiz.id.toString().startsWith('id_')) {
                                setIsDownloading(true);
                                try {
                                    await downloadDocxFromBackend('quiz', quiz.id, `${finalTitle}_DapAn.docx`, { variant: 'answers' });
                                } catch {
                                    await handleDownloadQuizDocx(quiz, true, false, '_DapAn');
                                } finally {
                                    setIsDownloading(false);
                                }
                            } else {
                                await handleDownloadQuizDocx(quiz, true, false, '_DapAn');
                            }
                        }}>Đáp án (Word — chỉnh sửa)</DropdownItem>
                        <DropdownItem onClick={async () => { 
                            setIsOpen(false);
                            if (!quiz.id || quiz.id.toString().startsWith('id_')) {
                                alert('Vui lòng lưu bài kiểm tra trước khi tải PDF. PDF của bài kiểm tra được xuất từ backend để tránh lỗi cắt trang.');
                                return;
                            }
                            setIsDownloading(true);
                            try {
                                await downloadPdfFromBackend('quiz', quiz.id, `${quiz.title}.pdf`);
                            } catch (e: any) {
                                const message = e?.message || 'Không thể tải PDF từ máy chủ.';
                                alert(`${message}\n\nPDF của bài kiểm tra không còn dùng cơ chế chụp ảnh để tránh lỗi cắt trang. Vui lòng thử lại sau hoặc tải DOCX trong lúc chờ xử lý.`);
                            } finally {
                                setIsDownloading(false);
                            }
                        }}>Tải Đề bài (PDF)</DropdownItem>
                        <DropdownItem onClick={() => { setIsOpen(false); handleDownloadGradingSheet(quiz); }}>Tải Phiếu chấm bài</DropdownItem>
                        <div className="border-t my-1"></div>
                        <DropdownItem onClick={() => { setIsOpen(false); handleDownloadMatrixOnly(quiz); }} disabled={!quiz.matrix || !Array.isArray(quiz.matrix) || quiz.matrix.length === 0}>Tải Ma trận đề</DropdownItem>
                        <DropdownItem onClick={() => { setIsOpen(false); handleDownloadTestRecord(quiz); }} disabled={!quiz.matrix || !Array.isArray(quiz.matrix) || quiz.matrix.length === 0}>Tải Biên bản ra đề</DropdownItem>
                        <DropdownItem onClick={() => { setIsOpen(false); handleDownloadSpecification(quiz); }} disabled={!quiz.specification || !Array.isArray(quiz.specification) || quiz.specification.length === 0}>Tải Bản đặc tả</DropdownItem>
                        <div className="border-t my-1"></div>
                        <DropdownItem onClick={async () => {
                            setIsOpen(false);
                            if (APP_MODE === 'PRODUCTION' && quiz.id && !quiz.id.toString().startsWith('id_')) {
                                setIsDownloading(true);
                                try {
                                    await downloadDocxFromBackend('quiz', quiz.id, `${finalTitle}_ToanBo.docx`, { variant: 'full' });
                                } catch {
                                    await handleDownloadAll(quiz);
                                } finally {
                                    setIsDownloading(false);
                                }
                            } else {
                                await handleDownloadAll(quiz);
                            }
                        }}>Toàn bộ (Word — chỉnh sửa)</DropdownItem>
                    </div>
                </div>
            )}
       </div>
    );
  }

  const handleUpgradeHtml5 = async (presData: Presentation, force: boolean = false) => {
    if (!presData.id || String(presData.id).startsWith('id_')) {
        alert("Vui lòng lưu bài trình chiếu trước khi tạo bản xem.");
        return;
    }

    if (isEditing) {
        if (!confirm("Nội dung đang chỉnh sửa chưa được lưu. Bạn muốn mở bản xem của lần lưu cuối cùng chứ?")) {
            return;
        }
    }

    setIsUpgrading(true);
    try {
        const { html_url, share_token, public_html5_url } = await upgradePresentationToHtml5(presData.id, force);
        if (!html_url && !public_html5_url) {
            throw new Error("Máy chủ không trả về liên kết trình chiếu.");
        }

        // Sau khi nâng cấp thành công, nếu có callback onUpgradeSuccess, hãy gọi nó để cập nhật state ở cha
        if (onUpgradeSuccess) {
            // Chúng ta có thể fetch lại resource mới nhất từ backend
            const freshPres = await fetchResourceById('presentations', presData.id, true);
            if (freshPres) {
                onUpgradeSuccess(freshPres);
            }
        }

        // Mở URL trình chiếu trong tab mới
        // Sử dụng token từ phản hồi hoặc từ dữ liệu hiện có
        const token = share_token || presData.share_token;
        let finalUrl = public_html5_url || html_url;
        if (token) {
            finalUrl += (finalUrl.includes('?') ? '&' : '?') + `token=${token}`;
        }

        const newWindow = window.open(finalUrl, '_blank', 'noopener,noreferrer');
        if (!newWindow) {
            alert("Trình duyệt đã chặn cửa sổ bật lên. Vui lòng kiểm tra cài đặt trình duyệt để xem bài trình chiếu.");
        }
    } catch (error) {
        console.error('Error upgrading to HTML5:', error);
        alert(error instanceof Error ? error.message : "Lỗi khi tạo bản xem trình chiếu.");
    } finally {
        setIsUpgrading(false);
    }
  };

  const refreshPresentationState = async (presData: Presentation) => {
    if (!presData?.id) return presData;
    const freshPres = await fetchResourceById('presentations', presData.id, true);
    if (freshPres && onUpgradeSuccess) {
      onUpgradeSuccess(freshPres);
    }
    return (freshPres || presData) as Presentation;
  };

  const openHtml5ShareModal = (presData: Presentation) => {
    if (!presData?.id || String(presData.id).startsWith('id_')) {
      alert('Vui lòng lưu bài trình chiếu trước khi chia sẻ.');
      return;
    }
    if (!(presData.html5Path ?? presData.html5_path)) {
      alert('Bạn cần tạo bản xem trước khi chia sẻ.');
      return;
    }
    setHtml5ShareOpen(true);
  };

  if (presentation) {
    const hasHtml5 = !!(presentation.html5Path ?? presentation.html5_path);

    return (
       <>
       {html5ShareOpen && (
         <ShareModal
           isOpen={true}
           onClose={() => setHtml5ShareOpen(false)}
           resourceType="presentations"
           resourceId={presentation.id}
           resourceTitle={presentation.title}
           currentVisibility={(presentation as any).visibility}
           currentShareToken={(presentation as any).shareToken ?? (presentation as any).share_token}
           currentShareScope={(presentation as any).shareScope ?? (presentation as any).share_scope}
           classes={userSettings?.classes}
           user={userSettings}
           onShowToast={onShowToast}
          buildShareLink={({ resourceId, token }) => {
            const publicHtml5Url =
              (presentation as any).publicHtml5Url ?? (presentation as any).public_html5_url ?? null;
            if (publicHtml5Url) {
              return token ? `${publicHtml5Url}?token=${encodeURIComponent(token || '')}` : publicHtml5Url;
            }
            return `${window.location.origin}/api/edu/view-slide/${resourceId}?token=${encodeURIComponent(token || '')}`;
          }}
           onUpdated={async () => {
             await refreshPresentationState(presentation);
           }}
         />
       )}
       <div className="flex flex-wrap items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white/85 p-3 shadow-sm backdrop-blur-sm">
          <button 
            onClick={() => handleDownloadPptx(presentation)} 
            disabled={isDownloading || isUpgrading}
            title="Tải xuống dưới dạng PowerPoint chuyên nghiệp (cho máy tính)" 
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-orange-200 bg-orange-50 px-4 text-sm font-semibold text-orange-700 transition-colors hover:bg-orange-100 disabled:cursor-not-allowed disabled:border-orange-100 disabled:bg-orange-50 disabled:text-orange-300"
          >
            {isDownloading ? <Spinner size="sm" /> : <ArrowDownTrayIcon className="h-4 w-4" />}
            <span>Tải PPTX</span>
          </button>

          {!hasHtml5 ? (
            <button 
                onClick={() => handleUpgradeHtml5(presentation, false)} 
                disabled={isDownloading || isUpgrading}
                title="Tạo bản xem trình chiếu" 
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 px-4 text-sm font-semibold text-white shadow-sm transition-all hover:from-teal-700 hover:to-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
            >
                {isUpgrading ? <Spinner size="sm" /> : <SparklesIcon className="h-4 w-4 text-yellow-300" />}
                <span>Tạo bản xem trình chiếu</span>
            </button>
          ) : (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsOpen(p => !p)}
                disabled={isDownloading || isUpgrading}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-teal-600 px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
                title="Các thao tác cho trình chiếu"
              >
                {isUpgrading || isDownloading ? <Spinner size="sm" /> : <SparklesIcon className="h-4 w-4 text-yellow-300" />}
                <span>{isDownloading ? 'Đang tải...' : 'Thao tác trình chiếu HTML'}</span>
                <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>

              {isOpen && !isDownloading && !isUpgrading && (
                <div className="absolute bottom-full right-0 z-20 mb-2 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                  <div className="py-1">
                    <DropdownItem onClick={() => { setIsOpen(false); handleUpgradeHtml5(presentation, false); }}>
                      Xem bản trình chiếu                     </DropdownItem>
                    <DropdownItem onClick={() => { setIsOpen(false); handleDownloadPptxScreenshots(presentation); }}>
                      Tải PPTX (Bản chụp HTML)
                    </DropdownItem>
                    <DropdownItem onClick={() => { 
                      setIsOpen(false);
                      if (confirm("Bạn có chắc chắn muốn cập nhật lại bản xem? Thao tác này sẽ tạo lại bản trình chiếu từ dữ liệu hiện tại và tốn thêm tín dụng.")) {
                        handleUpgradeHtml5(presentation, true);
                      }
                    }}>
                      Cập nhật bản trình chiếu mới
                    </DropdownItem>
                    <DropdownItem onClick={() => { setIsOpen(false); openHtml5ShareModal(presentation); }}>
                      Chia sẻ bài trình chiếu 
                    </DropdownItem>
                  </div>
                </div>
              )}
            </div>
          )}
       </div>
       </>
    );
  }

  if (lessonPlan) {
    return (
        <div className="relative" ref={dropdownRef}>
            <button 
                onClick={() => setIsOpen(p => !p)} 
                disabled={isDownloading}
                className="flex items-center justify-center space-x-2 font-medium px-5 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:bg-indigo-400 disabled:cursor-not-allowed"
            >
                {isDownloading ? <Spinner size="sm" /> : <ArrowDownTrayIcon className="h-5 w-5" />}
                <span>Tải xuống</span>
                <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
             {isOpen && !isDownloading && (
                <div className="absolute bottom-full mb-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1">
                        <DropdownItem onClick={async () => { 
                            setIsOpen(false); 
                            if (APP_MODE === 'PRODUCTION' && lessonPlan.id && !lessonPlan.id.toString().startsWith('id_')) {
                                setIsDownloading(true);
                                try {
                                    await downloadPdfFromBackend('lesson-plan', lessonPlan.id, `${lessonPlan.title}.pdf`);
                                } catch (e) {
                                    handleDownloadPdfLessonPlan(lessonPlan);
                                } finally {
                                    setIsDownloading(false);
                                }
                            } else {
                                handleDownloadPdfLessonPlan(lessonPlan); 
                            }
                        }}>Tải Giáo án (PDF)</DropdownItem>
                        {contentElementId ? (
                        <DropdownItem onClick={() => { setIsOpen(false); handleDownloadPdfGeneric(); }}>Tải Giáo án (PDF - Ảnh)</DropdownItem>
                        ) : null}
                        <DropdownItem onClick={() => { setIsOpen(false); handleDownloadDocxLessonPlan(lessonPlan); }}>Tải Giáo án (DOCX)</DropdownItem>
                    </div>
                </div>
            )}
        </div>
    );
  }

  if (activity) {
    return (
        <div className="relative" ref={dropdownRef}>
            <button 
                onClick={() => setIsOpen(p => !p)} 
                disabled={isDownloading}
                className="flex items-center justify-center space-x-2 font-medium px-5 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:bg-indigo-400 disabled:cursor-not-allowed"
            >
                {isDownloading ? <Spinner size="sm" /> : <ArrowDownTrayIcon className="h-5 w-5" />}
                <span>Tải xuống</span>
                <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && !isDownloading && (
                <div className="absolute bottom-full mb-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1">
                        <DropdownItem onClick={async () => { 
                            setIsOpen(false); 
                            if (APP_MODE === 'PRODUCTION' && activity.id && !activity.id.toString().startsWith('id_')) {
                                setIsDownloading(true);
                                try {
                                    await downloadPdfFromBackend('activity', activity.id, `${activity.title}.pdf`);
                                } catch (e) {
                                    handleDownloadPdfGeneric();
                                } finally {
                                    setIsDownloading(false);
                                }
                            } else {
                                handleDownloadPdfGeneric(); 
                            }
                        }}>Tải Hoạt động (PDF)</DropdownItem>
                        {contentElementId ? (
                        <DropdownItem onClick={() => { setIsOpen(false); handleDownloadPdfGeneric(); }}>Tải Hoạt động (PDF - Ảnh)</DropdownItem>
                        ) : null}
                        <DropdownItem onClick={() => { setIsOpen(false); handleDownloadDocxGeneric(); }}>Tải Hoạt động (DOCX)</DropdownItem>
                    </div>
                </div>
            )}
        </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
        <button 
            onClick={() => setIsOpen(p => !p)} 
            disabled={isDownloading}
            className="flex items-center justify-center space-x-2 font-medium px-5 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:bg-indigo-400 disabled:cursor-not-allowed"
        >
            {isDownloading ? <Spinner size="sm" /> : <ArrowDownTrayIcon className="h-5 w-5" />}
            <span>Tải xuống</span>
            <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && !isDownloading && (
            <div className="absolute bottom-full mb-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1">
                    <DropdownItem onClick={() => { setIsOpen(false); handleDownloadPdfGeneric(); }}>
                      {skknExport ? 'Tải PDF (văn bản, font Unicode)' : 'Tải xuống (PDF)'}
                    </DropdownItem>
                    <DropdownItem onClick={() => { setIsOpen(false); handleDownloadDocxGeneric(); }}>
                      {skknExport ? 'Tải DOCX (có cấu trúc & bảng)' : 'Tải xuống (DOCX)'}
                    </DropdownItem>
                </div>
            </div>
        )}
    </div>
  );
};